import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../config/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // token-state ist optional; der Wrapper nutzt localStorage direkt via Interceptor
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Nie mehr axios.defaults setzen; Header wird vom Interceptor im api.js gesetzt

  const AUTH_TTL = 5 * 60 * 1000; // 5 minutes

  const clearAuth = (msg) => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_last_check');
    } catch (e) { }
    setToken(null);
    setUser(null);
    if (msg) toast.error(msg);
  };

  const persistUserCache = (u) => {
    try {
      localStorage.setItem('auth_user', JSON.stringify(u));
      localStorage.setItem('auth_last_check', String(Date.now()));
    } catch (e) {
      // ignore storage errors
    }
  };

  // Public method: refresh auth immediately (used after profile save and on SSE events).
  const refreshAuth = async ({ force = false } = {}) => {
    if (!localStorage.getItem('token')) {
      setUser(null);
      return { ok: true, user: null };
    }

    const now = Date.now();
    const lastCheck = Number(localStorage.getItem('auth_last_check')) || 0;
    const cachedUser = JSON.parse(localStorage.getItem('auth_user') || 'null');
    if (!force && cachedUser && now - lastCheck < AUTH_TTL) {
      setUser(cachedUser);
      return { ok: true, user: cachedUser, cached: true };
    }

    try {
      const response = await api.get('/api/auth/me');
      const u = response?.data?.user || null;
      if (!u) {
        clearAuth();
        return { ok: false, user: null };
      }

      if (u?.status === 'banned') {
        clearAuth('Аккаунт заблокирован. Пожалуйста, свяжитесь со службой поддержки.');
        return { ok: false, user: null };
      }

      setUser(u);
      persistUserCache(u);
      return { ok: true, user: u };
    } catch (error) {
      // Do NOT immediately clear token on transient errors.
      console.error('Auth refresh failed:', error);
      return { ok: false, user: cachedUser || null, error };
    }
  };

  // Initial auth check on load with TTL cache
  useEffect(() => {
    const run = async () => {
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      // Fast path: set cached user to avoid blank UI while refresh happens.
      try {
        const cachedUser = JSON.parse(localStorage.getItem('auth_user') || 'null');
        if (cachedUser) setUser(cachedUser);
      } catch (_) { }

      await refreshAuth({ force: false });
      setLoading(false);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // SSE: listen for auth updates pushed by server (admin changed role/entitlements)
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    let abort = null;
    let retryTimer = null;

    const connect = async () => {
      try {
        // Use fetch streaming to support Authorization header (EventSource can't set headers).
        const ctrl = new AbortController();
        abort = () => ctrl.abort();

        const res = await fetch('/api/auth/stream', {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
          signal: ctrl.signal
        });
        if (!res.ok || !res.body) throw new Error(`SSE connect failed: ${res.status}`);

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!cancelled) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // Parse SSE blocks separated by double newline
          let idx;
          // eslint-disable-next-line no-cond-assign
          while ((idx = buffer.indexOf('\n\n')) >= 0) {
            const raw = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);

            const lines = raw.split('\n');
            let eventName = 'message';
            let dataStr = '';
            for (const line of lines) {
              if (line.startsWith('event:')) eventName = line.slice(6).trim();
              if (line.startsWith('data:')) dataStr += line.slice(5).trim();
            }

            if (eventName === 'auth_changed') {
              // Force refresh -> updates user/role/entitlements immediately
              await refreshAuth({ force: true });
            }
          }
        }
      } catch (e) {
        if (cancelled) return;
        // Retry with backoff (lightweight, avoids tight loops)
        const delay = 3000;
        retryTimer = setTimeout(connect, delay);
      }
    };

    connect();
    return () => {
      cancelled = true;
      try { if (abort) abort(); } catch (_) { }
      try { if (retryTimer) clearTimeout(retryTimer); } catch (_) { }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/api/auth/login', { email, password });
      const { user, token } = response.data;

      // Prevent banned users from logging in
      if (user?.status === 'banned') {
        // Do not persist token or user on client
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('auth_user');
          localStorage.removeItem('auth_last_check');
        } catch (e) { }
        setToken(null);
        setUser(null);
        toast.error('Аккаунт заблокирован. Вход невозможен.');
        return { success: false, error: 'Account banned' };
      }

      // Normal login flow
      localStorage.setItem('token', token);
      try {
        localStorage.setItem('auth_user', JSON.stringify(user));
        localStorage.setItem('auth_last_check', String(Date.now()));
      } catch (e) { }
      setToken(token);
      setUser(user);
      // refresh cache timestamp for /api/auth/me
      try {
        localStorage.setItem('auth_last_check', String(Date.now()));
      } catch (e) { }
      toast.success('Вход выполнен');
      return { success: true };
    } catch (error) {
      const message = error?.response?.data?.error || 'Не удалось войти';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (email, password, username, accountType = 'profile') => {
    try {
      const response = await api.post('/api/auth/register', {
        email,
        password,
        username,
        account_type: accountType
      });
      const { user, token } = response.data;

      // If server returns a token, perform auto-login behavior.
      if (token) {
        // Prevent auto-login if server returns a banned user (defensive)
        if (user?.status === 'banned') {
          try {
            localStorage.removeItem('token');
            localStorage.removeItem('auth_user');
            localStorage.removeItem('auth_last_check');
          } catch (e) { }
          setToken(null);
          setUser(null);
          toast.error('Аккаунт заблокирован. Регистрация завершена, но вход невозможен.');
          return { success: false, error: 'Account banned' };
        }

        localStorage.setItem('token', token);
        try {
          localStorage.setItem('auth_user', JSON.stringify(user));
          localStorage.setItem('auth_last_check', String(Date.now()));
        } catch (e) { }
        setToken(token);
        setUser(user);
        toast.success('Аккаунт создан, вход выполнен');
      } else {
        // No token returned: require email verification before login
        toast.success('Аккаунт создан. Проверьте почту для кода подтверждения.');
      }

      return { success: true };
    } catch (error) {
      const message = error?.response?.data?.error || 'Не удалось зарегистрироваться';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    try {
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_last_check');
    } catch (e) { }
    setToken(null);
    setUser(null);
    toast.success('Вы вышли из аккаунта');
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/api/users/profile', profileData);
      setUser(response.data.user);
      toast.success('Профиль обновлён');
      return { success: true };
    } catch (error) {
      const message = error?.response?.data?.error || 'Не удалось обновить профиль';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const isSuperAdmin = user && (user.role === 'super_admin' || user.is_super_admin === true);
  const isAdmin = !!(user && (isSuperAdmin || String(user.role || '').toLowerCase() === 'admin' || String(user.role || '').toLowerCase() === 'админ'));

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshAuth,
    updateProfile,
    isAuthenticated: !!user,
    isSuperAdmin,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
